import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faLink } from '@fortawesome/pro-light-svg-icons/faLink';
import { TypedMemo } from '../../../common/types';
import { ShareErrorKind } from './assetShareState';
import { AssetShareModel } from './useAssetShare';
import { viewerMessages } from './viewerMessages';
import { Panel, PanelBody, PanelHead } from '../../components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the viewer; each part uses its own slice of it */
import styles from './AssetViewer.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ViewerShareBoxProps {
  model: AssetShareModel;
}

const ERROR_MESSAGES: Record<ShareErrorKind, keyof typeof viewerMessages> = {
  notReady: 'errorNotReady',
  failed: 'errorFailed',
  turnOffFailed: 'errorTurnOff',
};

/**
 * The public link of the asset: off (a1), on with the address, Copy, the date, "Open the page" and
 * "Turn off the link" (a2), turned off (a4), loading and the error line (a5). Every member of the
 * workspace may share (D7). The watermark of a file is not said here: the callout of the side panel
 * says it, and "Share link" and "Copy" open the choice first (design: trial-upsell a2, a3).
 */
const ViewerShareBoxComponent: React.FC<ViewerShareBoxProps> = ({ model }) => {
  const intl = useIntl();
  const { status, share, error } = model;
  const isOn = status === 'on' && share !== null;

  return (
    <Panel nested as="section" aria-live="polite" data-testid={`viewer-share-${status}`}>
      <PanelHead
        titleAs="h4"
        icon={<span className={isOn ? styles.dotOn : styles.dotOff} aria-hidden="true" />}
        title={intl.formatMessage(isOn ? viewerMessages.shareOnTitle : viewerMessages.shareTitle)}
      />
      <PanelBody stack className={styles.share}>
        {status === 'loading' && <div className={styles.skeleton} />}
        {isOn && share !== null && (
          <>
            <div className={styles.link}>
              <input
                className={styles.linkField}
                value={share.url}
                readOnly
                aria-label={intl.formatMessage(viewerMessages.linkAddress)}
                onFocus={(event) => event.currentTarget.select()}
              />
              <button type="button" className={styles.btn} onClick={model.copy}>
                {model.justCopied && <FontAwesomeIcon icon={faCheck} />}
                {intl.formatMessage(model.justCopied ? viewerMessages.copied : viewerMessages.copy)}
              </button>
            </div>
            <p className={styles.small}>
              {intl.formatMessage(viewerMessages.sharedOn, {
                date: intl.formatDate(share.createdAt, { day: 'numeric', month: 'short', year: 'numeric' }),
              })}
            </p>
            <div className={styles.linkRow}>
              <a
                className={clsx(styles.btn, styles.btnLink)}
                href={share.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {intl.formatMessage(viewerMessages.openPage)}
              </a>
              <button
                type="button"
                className={clsx(styles.btn, styles.btnDanger)}
                disabled={model.isTurningOff}
                onClick={model.askTurnOff}
              >
                {intl.formatMessage(viewerMessages.turnOff)}
              </button>
            </div>
          </>
        )}
        {(status === 'off' || status === 'turnedOff') && (
          <>
            <p className={styles.small}>
              {intl.formatMessage(status === 'off' ? viewerMessages.shareHint : viewerMessages.turnedOffNote)}
            </p>
            <button
              type="button"
              className={styles.btn}
              disabled={model.isCreating}
              onClick={model.create}
              data-testid="viewer-share-button"
            >
              <FontAwesomeIcon icon={faLink} />
              {intl.formatMessage(status === 'off' ? viewerMessages.shareLink : viewerMessages.shareNewLink)}
            </button>
          </>
        )}
        {error !== null && (
          <p className={styles.error} role="alert">
            {intl.formatMessage(viewerMessages[ERROR_MESSAGES[error]])}
          </p>
        )}
      </PanelBody>
    </Panel>
  );
};

export const ViewerShareBox = TypedMemo(ViewerShareBoxComponent);
